// AISettings.js - Konfigurasi AI + Multi-key rotation support
import React, { useState, useEffect } from 'react';
import {
  X, Save, Eye, EyeOff, ExternalLink, RotateCcw, Loader2,
  CheckCircle2, RefreshCw, Download, AlertCircle, Key, Copy, Check,
  Plus, Trash2, MessageSquare, LayoutDashboard, ChevronDown, Sparkles,
} from 'lucide-react';
import {
  AI_PROVIDERS, DEFAULT_SYSTEM_PROMPT, saveConfigToServer, saveDashboardConfig,
  getDefaultConfig, fetchModelsFromProvider, getKeyCount,
  CODE_KEY,
} from './aiConfig';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LanguageContext';

export default function AISettings({ config, dashboardKey, dashboardLabel, dashConfig, onSave, onDashSave, onClose, canConfigAI = true }) {
  const { user }             = useAuth();
  const { t }                = useLang();
  const [form, setForm]      = useState({ ...config });
  // Per-dashboard ATAU global (saat tanpa dashboardKey + boleh konfig): system prompt & quick prompts
  const [dashPrompt, setDashPrompt]   = useState(dashConfig?.system_prompt || '');
  const [quickPrompts, setQuickPrompts] = useState((dashboardKey ? dashConfig?.quick_prompts : config?.quickPrompts) || null);
  const [showKey, setShowKey]   = useState(false);
  // [BEDROCK-CRED] Field kredensial AWS dibuat readOnly sampai di-fokus. Chrome
  // & password-manager mengabaikan autoComplete="off", tapi tidak mengisi field
  // readOnly — ini mencegah username/password login masuk ke Access Key ID.
  const [credLock, setCredLock] = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState(null);
  const [copied, setCopied]     = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch models state
  const [fetchingModels, setFetchingModels] = useState(false);
  const [fetchedModels,  setFetchedModels]  = useState(null);
  const [fetchError,     setFetchError]     = useState(null);

  // [CODE MODEL] Fetch model khusus Coding Mode — state TERPISAH dari fetch
  // global supaya daftar dua provider yang berbeda tidak saling menimpa.
  const [codeFetching,  setCodeFetching]  = useState(false);
  const [codeModels,    setCodeModels]    = useState(null);
  const [codeFetchError, setCodeFetchError] = useState(null);

  const provider  = AI_PROVIDERS[form.provider] || AI_PROVIDERS.groq;

  // ── [CUSTOM2] Pemetaan slot → nama field ───────────────────────────────────
  // Dua slot Generic OpenAI menyimpan endpoint & model manualnya di field yang
  // BERBEDA (`baseUrl`/`baseUrl2`), karena keduanya harus bisa terisi bersamaan
  // — itu seluruh alasan slot kedua ada. Kalau UI menulis ke satu field yang
  // sama, memilih slot 2 lalu Save akan menghapus endpoint slot 1 dan
  // pergantian slot jadi tidak ada gunanya.
  //
  // Yang dipetakan adalah NAMA field, bukan nilainya, supaya `set()` menulis ke
  // tempat yang benar tanpa perlu cabang if di setiap onChange.
  const F_BASE_URL     = form.provider === 'openai_custom2' ? 'baseUrl2'     : 'baseUrl';
  const F_CUSTOM_MODEL = form.provider === 'openai_custom2' ? 'customModel2' : 'customModel';
  const F_LABEL        = form.provider === 'openai_custom2' ? 'customLabel2' : 'customLabel';
  const gBaseUrl     = form[F_BASE_URL] || '';
  const gCustomModel = form[F_CUSTOM_MODEL] || '';
  // Nama tampilan provider: label yang diisi admin bila ada, kalau tidak nama
  // bawaan deskriptor. Hanya berlaku untuk kedua slot custom — provider lain
  // punya nama tetap dan tidak punya kolom label.
  const providerLabel = (pid) => {
    if (pid === 'openai_custom'  && String(form.customLabel  || '').trim()) return form.customLabel.trim();
    if (pid === 'openai_custom2' && String(form.customLabel2 || '').trim()) return form.customLabel2.trim();
    return AI_PROVIDERS[pid]?.name || pid;
  };
  // [Phase 2] Key di-mask server-side. Bila admin belum mengetik ulang, tampilkan
  // status tersimpan (config.keyCount/hasKey). Mengetik key baru akan menggantikan.
  const typedKeyCount = getKeyCount(form.apiKey);
  const keyCount  = typedKeyCount || config.keyCount || 0;
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // ── Quick Prompts editor (per-dashboard, per-bahasa) ──────────────────────
  const QP_LANGS = ['id', 'en'];
  const [qpLang, setQpLang] = useState('en');
  // [A] Mode prompt GLOBAL: saat tak ada dashboardKey tapi user boleh konfig
  // (mis. halaman AI Configuration / AI Workspace "General"). System prompt &
  // quick prompts disimpan global di ai_configs, berlaku saat tak ada dashboard.
  const isGlobalPrompt = !dashboardKey && canConfigAI;
  // Panel yang dibuka DARI halaman AI Coding. Berbeda dari panel per-dashboard
  // lain: satu-satunya setelan global yang memang khusus milik halaman ini —
  // code_provider/code_model — ikut ditampilkan di sini.
  //
  // Nilainya TETAP global (ai_configs user_id=0), bukan per-dashboard. Karena
  // itu blok tersebut memakai peringatan tersendiri; tanpa itu, menempatkannya
  // di panel per-dashboard justru menyiratkan tiap dashboard bisa punya nilai
  // sendiri, dan itu tidak benar.
  const isCodePanel = canConfigAI && dashboardKey === CODE_KEY;
  const promptVal   = isGlobalPrompt ? (form.systemPrompt || '') : dashPrompt;
  const setPromptVal = (v) => (isGlobalPrompt ? setForm(p => ({ ...p, systemPrompt: v })) : setDashPrompt(v));
  // Key item disimpan sebagai `${dashboardKey}.data`; untuk global pakai `__global__.data`
  const qpKey = dashboardKey ? `${dashboardKey}.data` : (isGlobalPrompt ? '__global__.data' : null);
  // Sentinel: array ['__none__'] = sengaja dikosongkan (tampilkan NOL prompt di chat),
  // dibedakan dari [] / tak ada key = belum diatur (pakai built-in).
  const QP_NONE = '__none__';
  const rawItems = (lang) => (quickPrompts?.[lang]?.[qpKey] || []);
  const isCleared = (lang) => { const a = rawItems(lang); return a.length === 1 && a[0] === QP_NONE; };
  const getItems = (lang) => { const a = rawItems(lang); return isCleared(lang) ? [] : a; };
  const setItems = (lang, items) => {
    setQuickPrompts(prev => {
      const qp = prev ? JSON.parse(JSON.stringify(prev)) : {};
      if (!qp[lang]) qp[lang] = {};
      qp[lang][qpKey] = items.length ? items : [QP_NONE];
      return qp;
    });
  };
  const clearItems = (lang) => setItems(lang, []);
  const addItem    = (lang)      => setItems(lang, [...getItems(lang), '']);
  const removeItem = (lang, idx) => { const a = [...getItems(lang)]; a.splice(idx, 1); setItems(lang, a); };
  const updateItem = (lang, idx, val) => { const a = [...getItems(lang)]; a[idx] = val; setItems(lang, a); };
  const resetItems = (lang) => {
    setQuickPrompts(prev => {
      if (!prev?.[lang]) return prev;
      const qp = JSON.parse(JSON.stringify(prev));
      delete qp[lang][qpKey];
      return qp;
    });
  };

  const handleProviderChange = (pid) => {
    setFetchedModels(null);
    setFetchError(null);
    setShowKey(false);
    // [CUSTOM2] Field endpoint ditentukan oleh slot TUJUAN (pid), bukan slot
    // yang sedang aktif — saat ini berjalan, provider belum berganti di state.
    // Memakai form.provider di sini akan menulis nilai bawaan slot baru ke
    // field slot LAMA, menimpanya.
    const fBase = pid === 'openai_custom2' ? 'baseUrl2' : 'baseUrl';
    setForm(prev => ({
      ...prev,
      provider:    pid,
      model:       AI_PROVIDERS[pid].defaultModel,
      // base_url ini murni milik provider GLOBAL. Mode Coding punya kolom
      // sendiri (`code_base_url` / form.codeBaseUrl), jadi mengosongkan yang
      // ini tidak lagi bisa mematikan sesi Coding.
      //
      // Pengosongan HANYA menyentuh field slot tujuan. Slot custom yang lain
      // tidak ikut disentuh sama sekali, jadi berpindah antar dua slot custom
      // tidak pernah kehilangan endpoint.
      [fBase]:     AI_PROVIDERS[pid].requiresBaseUrl
        ? (prev[fBase] || AI_PROVIDERS[pid].baseUrlPlaceholder || '')
        : '',
    }));
  };

  // Batas efektif dari AI Service. Selama belum termuat, nilainya null dan
  // placeholder menampilkan "ikut env" TANPA angka — lebih baik tidak
  // menyebutkan angka daripada menyebutkan angka yang salah.
  const [aiLimits, setAiLimits] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/ai/code-roots')
      .then(r => { if (alive) setAiLimits(r.data?.limits || null); })
      .catch(() => { /* tanpa akses ai_coding atau service mati — biarkan null */ });
    return () => { alive = false; };
  }, []);

  // "ikut env (N)" hanya ditampilkan bila N benar-benar diketahui.
  const envHint = (key) => {
    const v = aiLimits?.[key];
    return Number.isFinite(Number(v)) ? `ikut env (${v})` : 'ikut env';
  };

  const iterCeiling = Number(aiLimits?.code_iter_ceiling) || 6;

  const loadDefaultPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const res = await api.get(`/api/ai/system-prompt/${user?.role || 'admin'}`);
      setPromptVal(res.data.systemPrompt);
    } catch {
      setPromptVal(DEFAULT_SYSTEM_PROMPT);
    } finally {
      setLoadingPrompt(false);
    }
  };

  useEffect(() => {
    // Kalau dashboard ini belum punya system prompt, tawarkan default sebagai awalan
    if (dashboardKey && !dashConfig?.system_prompt) {
      loadDefaultPrompt();
    }
  }, []); // eslint-disable-line

  const handleFetchModels = async () => {
    setFetchingModels(true);
    setFetchError(null);
    setFetchedModels(null);
    try {
      const models = await fetchModelsFromProvider({
        provider: form.provider, apiKey: form.apiKey,
        baseUrl: gBaseUrl, ollamaUrl: form.ollamaUrl,
        awsRegion: form.provider === 'bedrock2' ? form.awsRegion2 : form.awsRegion,
      });
      if (!models || models.length === 0) {
        setFetchError(t('ai.set.noModelsFound'));
      } else {
        setFetchedModels(models);
        if (!models.some(m => m.id === form.model)) set('model', models[0].id);
      }
    } catch (e) {
      setFetchError(e.response?.data?.error || e.message || t('ai.set.fetchModelFailed'));
    } finally {
      setFetchingModels(false);
    }
  };

  // [CODE MODEL] Ambil daftar model dari provider mode Coding. Provider efektif
  // = codeProvider bila diisi, kalau tidak ikut provider global. API key hanya
  // diteruskan bila providernya SAMA dengan global — key global milik provider
  // lain tidak akan valid, dan endpoint fetch-models akan memakai key tersimpan.
  const codeProviderKey = form.codeProvider || form.provider;
  // Field endpoint Coding dirender kapan pun provider Coding memerlukannya —
  // tidak lagi bergantung pada provider global, karena nilainya kini kolom
  // sendiri dan bukan lagi field yang sama dengan Base URL di atas.
  const codeNeedsBaseUrl = !!AI_PROVIDERS[codeProviderKey]?.requiresBaseUrl;
  // [CUSTOM2] Field endpoint Coding juga per-slot.
  const F_CODE_BASE_URL = codeProviderKey === 'openai_custom2' ? 'codeBaseUrl2' : 'codeBaseUrl';
  // Endpoint efektif jalur Coding: kolom sendiri bila diisi, kalau tidak
  // mewarisi base_url global — TAPI hanya bila provider Coding SAMA dengan
  // provider global.
  //
  // ⚠ Pewarisan tanpa syarat itu benar selagi hanya ada SATU slot custom, dan
  // menjadi salah begitu ada dua. Contohnya: global di gateway A
  // (openai_custom), Coding di gateway B (openai_custom2) tanpa endpoint
  // sendiri — dulu ia akan mewarisi endpoint gateway A lalu memanggilnya dengan
  // key gateway B. Hasilnya 401/404 dari sisi provider, galat yang tidak
  // menyebut konfigurasi sama sekali. Justru kombinasi itulah yang paling
  // mungkin dipakai orang saat menambah slot kedua.
  //
  // Syarat kesamaan ini SEJAJAR dengan apiKey di bawah, yang sejak awal memang
  // tidak dipinjamkan lintas provider dengan alasan yang sama.
  const codeSameProvider = codeProviderKey === form.provider;
  const codeBaseUrlEff = String(form[F_CODE_BASE_URL] || '').trim()
    || (codeSameProvider ? gBaseUrl.trim() : '');
  const showCodeBaseUrl  = codeNeedsBaseUrl && !!form.codeProvider;
  const handleFetchCodeModels = async () => {
    setCodeFetching(true);
    setCodeFetchError(null);
    setCodeModels(null);
    try {
      const sameAsGlobal = codeSameProvider;
      const models = await fetchModelsFromProvider({
        provider:  codeProviderKey,
        apiKey:    sameAsGlobal ? form.apiKey : '',
        // Endpoint efektif jalur Coding — bukan base_url global. Kalau kolom
        // Coding kosong ia mewarisi nilai global HANYA saat providernya sama
        // (lihat codeBaseUrlEff). Beda dari apiKey yang memang per-provider dan
        // tidak boleh dipinjamkan lintas provider.
        baseUrl:   codeNeedsBaseUrl ? codeBaseUrlEff : (sameAsGlobal ? gBaseUrl : ''),
        ollamaUrl: sameAsGlobal ? form.ollamaUrl : '',
        awsRegion: codeProviderKey === 'bedrock2' ? form.awsRegion2 : (sameAsGlobal ? form.awsRegion : ''),
      });
      if (!models || models.length === 0) setCodeFetchError(t('ai.set.noModelsFound'));
      else setCodeModels(models);
    } catch (e) {
      setCodeFetchError(e.response?.data?.error || e.message || t('ai.set.fetchModelFailed'));
    } finally {
      setCodeFetching(false);
    }
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      // 1) Shared config (provider/model/api_key) — GLOBAL saja (tanpa dashboardKey).
      //    Mode global: ikut simpan systemPrompt (form) + quickPrompts global.
      if (canConfigAI && !dashboardKey) {
        const corePayload = { ...form, quickPrompts };
        await saveConfigToServer(corePayload);
      } else if (isCodePanel) {
        // Panel Coding boleh mengubah code_provider/code_model, jadi config
        // global HARUS ikut disimpan. Tanpa cabang ini, dropdown-nya bisa
        // diubah, tombol Simpan menjawab "berhasil", dan nilainya tidak pernah
        // sampai ke server — persis pola bug yang sudah pernah terjadi pada
        // PUT /api/roles/:id.
        //
        // quickPrompts di state ini milik DASHBOARD, bukan global. Mengirimnya
        // akan MENIMPA quick prompts global: server menulis '' untuk nilai
        // kosong (server.js:612), jadi kekeliruan ini menghapus data, bukan
        // sekadar salah simpan. Yang dikirim adalah nilai global yang dimuat.
        //
        // Field lain di `form` tidak berubah sejak dimuat, jadi ikut terkirim
        // sebagai nilai yang sama. api_key kosong dipertahankan server
        // (server.js:620), sehingga kunci tidak terhapus.
        await saveConfigToServer({ ...form, quickPrompts: config.quickPrompts || null });
      }
      // 2) Per-dashboard config (system_prompt + quick_prompts)
      if (dashboardKey) {
        await saveDashboardConfig(dashboardKey, {
          system_prompt: dashPrompt,
          quick_prompts: quickPrompts,
        });
        if (onDashSave) onDashSave({ system_prompt: dashPrompt, quick_prompts: quickPrompts });
      }
      onSave(isGlobalPrompt ? { ...form, quickPrompts } : form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      onSave(form);
      setError(t('ai.set.savedLocal') + ' ' + (e.response?.data?.error || e.message));
      setSaved(true);
      setTimeout(() => { setSaved(false); setError(null); }, 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyFirstKey = () => {
    const keys = (form.apiKey || '').split(/[\n,]/).map(k => k.trim()).filter(Boolean);
    if (keys[0]) { navigator.clipboard.writeText(keys[0]); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  // [MODEL-ECHO] `fetchedModels` adalah state LOKAL — null setiap panel dibuka,
  // dan tidak ada auto-fetch. Untuk provider yang daftar statisnya hanya berisi
  // sentinel (mis. openai_custom → [{ id: 'auto' }]), nilai tersimpan seperti
  // `claude-haiku-4-5-20251001` tidak punya <option> yang cocok. Browser lalu
  // membiarkan selectedIndex=0, sehingga opsi PERTAMA yang tampil — terlihat
  // seperti setelan "hilang" padahal `form.model` (dan DB) masih benar.
  //
  // Karena itu nilai tersimpan selalu disuntikkan sebagai opsi bila belum ada.
  // Jangan menggantinya dengan `set('model', ...)` di useEffect: itu akan
  // MENIMPA nilai yang sah dengan sentinel.
  const baseModels = fetchedModels || provider.models;
  const displayModels =
    form.model && !baseModels.some(m => m.id === form.model)
      ? [{ id: form.model }, ...baseModels]
      : baseModels;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h3 className="font-semibold text-slate-800">{t('ai.set.title')}</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {t('ai.set.subtitle')}
            {user && <span className="ml-1 px-1.5 py-0.5 bg-brand-50 text-brand-600 rounded text-xs font-medium">({user.username})</span>}
          </p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
          <X style={{ width: 16, height: 16 }} className="text-slate-500" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

        {/* ── Bagian config inti (provider/model/key/parameter) — GLOBAL saja ──
            Sengaja disembunyikan di panel per-dashboard: model/provider/key
            bersifat global (satu untuk semua dashboard). Atur di AI Config global. */}
        {canConfigAI && !dashboardKey && (<>
        {/* Provider */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">{t('ai.set.provider')}</label>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(AI_PROVIDERS).map(([pid, p]) => (
              <button key={pid} onClick={() => handleProviderChange(pid)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all text-left
                  ${form.provider === pid ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                <span className="text-base">{p.logo}</span>
                <div className="min-w-0">
                  <div className="font-semibold text-xs truncate">{providerLabel(pid)}</div>
                  <div className="text-xs opacity-60">{p.requiresKey ? t('ai.set.apiKey') : t('ai.set.noKey')}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Label bebas — hanya untuk kedua slot Generic OpenAI. Tanpa ini daftar
            provider memaksa menebak mana yang sylorapi dan mana gateway lain,
            karena keduanya bernama hampir sama. */}
        {(form.provider === 'openai_custom' || form.provider === 'openai_custom2') && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Nama tampilan slot ini
            </label>
            <input type="text" value={form[F_LABEL] || ''}
              onChange={e => set(F_LABEL, e.target.value)}
              placeholder={AI_PROVIDERS[form.provider]?.name || ''}
              maxLength={40}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
            <p className="text-xs text-slate-400 mt-1.5">
              Muncul di daftar provider di atas. Kosong = pakai nama bawaan.
            </p>
          </div>
        )}

        {/* Base URL */}
        {provider.requiresBaseUrl && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">{t('ai.set.baseUrl')}</label>
            {/* Nama field dari F_BASE_URL, bukan literal 'baseUrl' — kedua slot
                custom menulis ke kolom masing-masing supaya keduanya bisa
                terisi bersamaan. */}
            <input type="text" value={gBaseUrl}
              onChange={e => set(F_BASE_URL, e.target.value)}
              placeholder={provider.baseUrlPlaceholder || 'http://localhost:1234'}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 font-mono" />
            <p className="text-xs text-slate-400 mt-1.5">
              💡 {t('ai.set.baseUrlHintPre')} <code>/v1/chat/completions</code> {t('ai.set.baseUrlHintPost')}
            </p>
          </div>
        )}

        {/* Ollama URL */}
        {form.provider === 'ollama' && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Ollama URL</label>
            <input type="text" value={form.ollamaUrl} onChange={e => set('ollamaUrl', e.target.value)}
              placeholder="http://localhost:11434"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 font-mono" />
          </div>
        )}

        {/* Model + Fetch */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Model</label>
            <button onClick={handleFetchModels} disabled={fetchingModels}
              className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 px-2 py-1 rounded border border-brand-200 hover:border-brand-300 transition-colors disabled:opacity-50">
              {fetchingModels
                ? <Loader2 style={{ width: 10, height: 10 }} className="animate-spin" />
                : <Download style={{ width: 10, height: 10 }} />}
              {fetchingModels ? t('ai.set.fetching') : t('ai.set.fetchModels')}
            </button>
          </div>

          <select value={form.model} onChange={e => set('model', e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100">
            {displayModels.map(m => (
              <option key={m.id} value={m.id}>
                {m.label && m.label !== m.id ? `${m.label} · ${m.id}` : m.id}
              </option>
            ))}
          </select>

          {((form.provider === 'ollama' && form.model === 'custom') ||
            (form.provider === 'bedrock' && form.model === 'custom') ||
            (form.provider === 'bedrock2' && form.model === 'custom') ||
            (form.provider === 'openai_custom' && form.model === 'auto') ||
            (form.provider === 'openai_custom2' && form.model === 'auto') ||
            (form.provider === 'lmstudio' && form.model === 'auto')) && (
            <input type="text" value={gCustomModel} onChange={e => set(F_CUSTOM_MODEL, e.target.value)}
              placeholder={t('aiSet2.modelIdContohUsAnthropic')}
              className="mt-2 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 font-mono" />
          )}
          {fetchedModels && (
            <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
              <CheckCircle2 style={{ width: 11, height: 11 }} />
              {t('ai.set.modelsFound', { n: fetchedModels.length })}
            </p>
          )}
          {fetchError && (
            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
              <AlertCircle style={{ width: 11, height: 11 }} />
              {fetchError}
            </p>
          )}
        </div>

        {/* ── [BEDROCK2] Second Bedrock slot: region only, credentials on server ── */}
        {form.provider === 'bedrock2' && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">AWS Bedrock #2</label>
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <label className="block text-sm text-slate-700 mb-1">Region</label>
              <input type="text" value={form.awsRegion2 || ''}
                onChange={e => set('awsRegion2', e.target.value.trim())}
                placeholder="ap-southeast-3"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none font-mono" />
              <p className="text-[11px] text-slate-500">
                Uses AWS account #2 (Indomas Account) credentials stored on the server (AWS_ACCT2_*).
                No key is entered here. Default region: ap-southeast-3 (Jakarta).
              </p>
            </div>
          </div>
        )}

        {/* ── AWS Bedrock config (region + kredensial opsional) ───────────── */}
        {form.provider === 'bedrock' && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">AWS Bedrock</label>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <div>
                <label className="block text-sm text-slate-700 mb-1">Region</label>
                <input type="text" value={form.awsRegion || ''}
                  onChange={e => set('awsRegion', e.target.value.trim())}
                  placeholder="us-east-1"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none font-mono" />
                <p className="text-[11px] text-slate-500 mt-1">{t('ai.set.bedrockRegionHint')}</p>
              </div>

              <div className="border-t border-slate-100 pt-3">
                <p className="text-[11px] text-slate-500 mb-2">
                  {t('ai.set.bedrockCredHint')}
                </p>
                <label className="block text-sm text-slate-700 mb-1">Access Key ID</label>
                <input type="text" value={form.awsAccessKeyId || ''}
                  name="bedrock-akid-x" id="bedrock-akid-x" autoComplete="off"
                  spellCheck={false} readOnly={credLock} onFocus={() => setCredLock(false)}
                  onChange={e => setForm(p => ({ ...p, awsAccessKeyId: e.target.value.trim(), awsClearCreds: false }))}
                  placeholder={t('ai.set.optional')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none font-mono mb-2" />
                <label className="block text-sm text-slate-700 mb-1">Secret Access Key</label>
                <input type={showKey ? 'text' : 'password'} value={form.awsSecretAccessKey || ''}
                  name="bedrock-asak-x" id="bedrock-asak-x" autoComplete="new-password"
                  spellCheck={false} readOnly={credLock} onFocus={() => setCredLock(false)}
                  onChange={e => setForm(p => ({ ...p, awsSecretAccessKey: e.target.value.trim(), awsClearCreds: false }))}
                  placeholder="•••••••••••••••• (opsional)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none font-mono" />
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <button type="button" onClick={() => setShowKey(v => !v)}
                    className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
                    {showKey ? <EyeOff style={{ width: 11, height: 11 }} /> : <Eye style={{ width: 11, height: 11 }} />}
                    {showKey ? t('ai.set.hide') : t('ai.set.show')} secret
                  </button>
                  {/* [BEDROCK-CRED] Hapus kredensial tersimpan → server memakai
                      IAM role / env (sama seperti integrasi Cost Explorer). */}
                  <button type="button"
                    onClick={() => setForm(p => ({
                      ...p, awsAccessKeyId: '', awsSecretAccessKey: '',
                      awsSessionToken: '', awsClearCreds: true,
                    }))}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                    <Trash2 style={{ width: 11, height: 11 }} />
                    Hapus kredensial (pakai IAM role)
                  </button>
                </div>
                {form.awsClearCreds && (
                  <p className="text-[11px] text-amber-600 mt-1.5">
                    Kredensial akan dihapus saat Save Configuration → Bedrock memakai IAM role server.
                  </p>
                )}
                {!form.awsClearCreds && config.hasAwsSecret && !(form.awsSecretAccessKey || '') && (
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Secret tersimpan di server (tidak ditampilkan). Kosongkan lewat tombol di atas bila ingin memakai IAM role.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        </>)}

        {/* ── Mode Coding: override provider/model ─────────────────────────
            Terpisah dari provider global supaya model yang kuat untuk kode bisa
            diuji TANPA mengganggu jalur data/text-to-SQL yang few-shot-nya sudah
            di-tune untuk llama. Kosong = ikut global (default, aman).

            DI LUAR gerbang !dashboardKey — sengaja. Blok ini satu-satunya bagian
            config global yang berlaku KHUSUS untuk dashboard Coding, sehingga
            menyembunyikannya dari halaman Coding berarti satu-satunya setelan
            milik halaman itu tidak bisa diatur dari sana. */}
        {(canConfigAI && (!dashboardKey || isCodePanel)) && (
        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            Coding Mode
          </label>
          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <p className="text-[11px] text-slate-500">
              Provider &amp; model khusus target <b>Coding</b> di AI Workspace. Biarkan
              &quot;Ikut global&quot; bila tidak ingin memisahkan.
            </p>
            {/* Hanya di panel halaman Coding. Panel ini bertajuk "prompt per
                dashboard", jadi tanpa catatan ini pembaca wajar menyangka nilai
                di bawah juga per-dashboard — padahal satu untuk semua sesi
                Coding, termasuk yang dibuka dari AI Workspace dan AI Panel. */}
            {isCodePanel && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                Setelan ini <b>global</b>, bukan per-dashboard: berlaku untuk semua
                sesi Coding (halaman ini, AI Workspace, dan AI Panel) — bukan hanya
                tab yang sedang dibuka. Tersimpan bersamaan saat menekan Simpan.
              </p>
            )}
            <div>
              <label className="block text-sm text-slate-700 mb-1">Provider</label>
              <select value={form.codeProvider || ''}
                onChange={e => { set('codeProvider', e.target.value); setCodeModels(null); setCodeFetchError(null); }}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none">
                <option value="">Ikut global ({providerLabel(form.provider)})</option>
                {Object.keys(AI_PROVIDERS).map(k => (
                  <option key={k} value={k}>{providerLabel(k)}</option>
                ))}
              </select>
            </div>

            {/* Field yang SAMA dengan Base URL di atas (satu kolom `base_url`),
                dirender di sini hanya ketika provider global tidak memerlukannya
                sehingga blok atas tersembunyi. Tanpa ini nilainya tak terlihat
                dan tak bisa diperbaiki dari UI. */}
            {showCodeBaseUrl && (
              <div>
                <label className="block text-sm text-slate-700 mb-1">{t('ai.set.baseUrl')}</label>
                <input type="text" value={form[F_CODE_BASE_URL] || ''}
                  onChange={e => set(F_CODE_BASE_URL, e.target.value)}
                  placeholder={(codeSameProvider && gBaseUrl)
                    ? `Ikut global (${gBaseUrl})`
                    : (AI_PROVIDERS[codeProviderKey]?.baseUrlPlaceholder || 'https://your-server.com')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none font-mono" />
                {!codeBaseUrlEff ? (
                  <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle style={{ width: 11, height: 11 }} />
                    Wajib diisi untuk {providerLabel(codeProviderKey)} — sesi Coding akan gagal bila kosong.
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Endpoint <b>khusus mode Coding</b>, terpisah dari Base URL provider global.
                    {codeSameProvider
                      ? ' Kosongkan untuk mengikuti nilai global.'
                      : ' Provider Coding berbeda dari global, jadi nilai global TIDAK diwarisi — wajib diisi di sini.'}
                  </p>
                )}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-slate-700">Model</label>
                <button type="button" onClick={handleFetchCodeModels} disabled={codeFetching}
                  className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 px-2 py-1 rounded border border-brand-200 hover:border-brand-300 transition-colors disabled:opacity-50">
                  {codeFetching
                    ? <Loader2 style={{ width: 10, height: 10 }} className="animate-spin" />
                    : <Download style={{ width: 10, height: 10 }} />}
                  {codeFetching ? t('ai.set.fetching') : t('ai.set.fetchModels')}
                </button>
              </div>

              {/* Daftar hasil fetch — muncul hanya setelah tombol ditekan. Input
                  teks di bawahnya tetap ada karena sebagian model tidak muncul
                  di daftar provider, dan agar nilai lama tetap bisa diedit. */}
              {codeModels && (
                <select value={codeModels.some(m => m.id === form.codeModel) ? form.codeModel : ''}
                  onChange={e => e.target.value && set('codeModel', e.target.value)}
                  className="w-full mb-2 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none">
                  <option value="">— pilih dari {codeModels.length} model {providerLabel(codeProviderKey)} —</option>
                  {codeModels.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.label && m.label !== m.id ? `${m.label} · ${m.id}` : m.id}
                    </option>
                  ))}
                </select>
              )}

              <input type="text" value={form.codeModel || ''}
                onChange={e => set('codeModel', e.target.value)}
                list="code-model-suggestions"
                placeholder={form.codeProvider ? 'wajib diisi bila provider dipilih' : `Ikut global (${form.model})`}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none font-mono" />
              <datalist id="code-model-suggestions">
                {(codeModels || AI_PROVIDERS[codeProviderKey]?.models || []).map(m => (
                  <option key={m.id} value={m.id} />
                ))}
              </datalist>
              {codeFetchError && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <AlertCircle style={{ width: 11, height: 11 }} />
                  {codeFetchError}
                </p>
              )}
              {/* Guard yang sama ada di AI Service: bila provider coding beda dari
                  global, model global TIDAK diwariskan — harus diisi di sini agar
                  tidak jatuh ke default provider yang mungkin bukan yang dimaksud. */}
              {form.codeProvider && form.codeProvider !== form.provider && !String(form.codeModel || '').trim() && (
                <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle style={{ width: 11, height: 11 }} />
                  Provider berbeda dari global — isi model, jika tidak akan dipakai model default provider.
                </p>
              )}
              <p className="text-[11px] text-slate-500 mt-1">
                Tool use (read_file / grep_code / pm2_logs) berjalan pada provider
                OpenAI-compatible (Groq, OpenAI, LM Studio), Bedrock, dan Anthropic.
                Gemini &amp; Ollama tidak mendukungnya. Analisa gambar hanya pada
                Bedrock &amp; Anthropic.
              </p>
            </div>

            {/* ── [CODE TUNING] Sampling khusus jalur Coding ──────────────────
                Kolomnya TERPISAH dari Parameter Generasi di bawah — itu bukan
                duplikasi. ai-service sengaja mengabaikan gen_params untuk mode
                code dan memakai AI_CODE_* dari env, karena default aiProxy
                (2000 token) memotong jawaban kode di tengah. Menumpang field
                lama berarti ikut mengubah jalur jawaban & SQL. */}
            <div className="pt-3 border-t border-slate-200 space-y-3">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Tuning Coding
              </label>
              <p className="text-[11px] text-slate-500">
                Kosongkan untuk mengikuti <code>AI_CODE_*</code> di ecosystem.config.js
                ai-service. Nilai di sini menang atas env.
              </p>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-600 mb-1">Max tokens</label>
                  <input type="number" min={0} max={16000} step={100}
                    value={form.codeMaxTokens || ''}
                    onChange={e => set('codeMaxTokens', Number(e.target.value) || 0)}
                    placeholder={envHint('code_max_tokens')}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-1">Temperature</label>
                  {/* -1 = ikut env, ditampilkan KOSONG. Menampilkan "-1" akan
                      terbaca seperti nilai yang sedang berlaku. */}
                  <input type="number" min={0} max={2} step={0.1}
                    value={form.codeTemperature >= 0 ? form.codeTemperature : ''}
                    onChange={e => set('codeTemperature',
                      e.target.value === '' ? -1 : Math.min(2, Math.max(0, Number(e.target.value))))}
                    placeholder={envHint('code_temperature')}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-1">Iterasi tool</label>
                  {/* min/max & clamp DITURUNKAN dari server (code_iter_ceiling),
                      bukan ditulis ulang di sini. Angka 6 yang dulu di-hardcode
                      di dua tempat membuat nilai di atasnya dipangkas server
                      tanpa gejala apa pun. */}
                  <input type="number" min={0} max={iterCeiling} step={1}
                    value={form.codeToolMaxIter || ''}
                    onChange={e => set('codeToolMaxIter', Math.min(iterCeiling, Number(e.target.value) || 0))}
                    placeholder={envHint('code_tool_max_iter')}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              </div>

              {/* ── Anggaran TPM ────────────────────────────────────────────
                  Groq menghitung max_tokens yang DIMINTA ke kuota TPM, bukan
                  yang terpakai. Karena itu permintaan satu kata pun bisa ditolak
                  kalau max_tokens-nya besar — dan pesan errornya justru menyuruh
                  "perkecil pesan", yang menyesatkan. Overhead ~2.100 diambil dari
                  kasus nyata: Groq melaporkan 10.108 diminta saat max_tokens 8.000. */}
              {(() => {
                // Fallback mengikuti nilai env EFEKTIF. Sebelumnya 8000
                // ditulis mati di sini, sehingga estimasi ikut salah begitu
                // AI_CODE_MAX_TOKENS berbeda.
                const mt = Number(form.codeMaxTokens) || Number(aiLimits?.code_max_tokens) || 8000;
                const est = mt + 2100;
                const isGroq = codeProviderKey === 'groq';
                const over = isGroq && est > 12000;
                return (
                  <div className={`text-[11px] rounded-md px-2 py-1.5 border ${over
                    ? 'text-red-700 bg-red-50 border-red-200'
                    : 'text-slate-600 bg-slate-50 border-slate-200'}`}>
                    Perkiraan tiap permintaan: <b>~{est.toLocaleString('en-US')} token</b>
                    {' '}({mt.toLocaleString('en-US')} max_tokens + ~2.100 system prompt &amp; skema tool).
                    {isGroq && (
                      <> Groq menghitung max_tokens yang <b>diminta</b> ke kuota TPM,
                      bukan yang terpakai. Free tier umumnya 12.000 TPM, tapi sebagian
                      model lebih rendah — <code>openai/gpt-oss-120b</code> hanya 8.000.
                      Bila angka di atas melampaui TPM model, permintaan ditolak
                      walau pesannya satu kata.</>
                    )}
                  </div>
                );
              })()}

              {Number(form.codeToolMaxIter) > Math.floor(iterCeiling / 2) && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                  Iterasi &gt; 4: tiap iterasi mengirim ulang <b>seluruh konteks</b>,
                  termasuk isi file yang sudah dibaca. Token per pertanyaan naik cepat —
                  dan pada provider berbayar, biayanya ikut naik.
                </p>
              )}
            </div>
          </div>
        </div>

)}

        {canConfigAI && !dashboardKey && (<>
        {/* ── Parameter Generasi ──────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t('ai.set.params')}</label>
            <button type="button"
              onClick={() => {
                set('temperature', 0.2); set('topP', 1.0); set('maxTokens', 2000);
                set('frequencyPenalty', 0); set('presencePenalty', 0); set('seed', null);
              }}
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
              <RotateCcw style={{ width: 11, height: 11 }} /> {t('ai.set.resetDefault')}
            </button>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-4">
            {/* Preset cepat */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles style={{ width: 12, height: 12 }} className="text-brand-500" />
                <span className="text-xs font-medium text-slate-600">{t('ai.set.preset')}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'precise', label: t('ai.set.presetPrecise'),  t: 0.1, p: 1.0, desc: t('ai.set.presetPreciseDesc') },
                  { key: 'balanced', label: t('ai.set.presetBalanced'), t: 0.5, p: 0.95, desc: t('ai.set.presetBalancedDesc') },
                  { key: 'creative', label: t('ai.set.presetCreative'),  t: 0.9, p: 0.95, desc: t('ai.set.presetCreativeDesc') },
                ].map(ps => {
                  const active = Math.abs((form.temperature ?? 0.2) - ps.t) < 0.001
                    && Math.abs((form.topP ?? 1.0) - ps.p) < 0.001;
                  return (
                    <button key={ps.key} type="button"
                      onClick={() => { set('temperature', ps.t); set('topP', ps.p); }}
                      title={ps.desc}
                      className={`px-2 py-1.5 rounded-lg border text-xs font-medium transition-all
                        ${active ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}>
                      {ps.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Temperature */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-700">Temperature</span>
                <span className="text-sm font-mono font-semibold text-brand-600">
                  {Number(form.temperature ?? 0.2).toFixed(1)}
                </span>
              </div>
              <input type="range" min="0" max="2" step="0.1"
                value={form.temperature ?? 0.2}
                onChange={e => set('temperature', parseFloat(e.target.value))}
                className="w-full accent-brand-600" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>{t('ai.set.tempLow')}</span>
                <span>{t('ai.set.tempHigh')}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                {t('ai.set.tempHint')}
              </p>
            </div>

            {/* Top P */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-700">Top P (nucleus sampling)</span>
                <span className="text-sm font-mono font-semibold text-brand-600">
                  {Number(form.topP ?? 1.0).toFixed(2)}
                </span>
              </div>
              <input type="range" min="0" max="1" step="0.05"
                value={form.topP ?? 1.0}
                onChange={e => set('topP', parseFloat(e.target.value))}
                className="w-full accent-brand-600" />
              <p className="text-[11px] text-slate-500 mt-1">
                {t('ai.set.topPHint')}
              </p>
            </div>

            {/* Max tokens */}
            <div>
              <label className="block text-sm text-slate-700 mb-1">{t('ai.set.maxTokens')}</label>
              <input type="number" min="100" max="8000" step="100"
                value={form.maxTokens ?? 2000}
                onChange={e => set('maxTokens', parseInt(e.target.value, 10) || 0)}
                onBlur={e => {
                  const n = parseInt(e.target.value, 10);
                  set('maxTokens', Number.isFinite(n) ? Math.min(8000, Math.max(100, n)) : 2000);
                }}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none" />
              <p className="text-[11px] text-slate-500 mt-1">{t('ai.set.maxTokensHint')}</p>
            </div>

            {/* Mode Grafik */}
            <div>
              <label className="block text-sm text-slate-700 mb-1">{t('ai.set.chart')}</label>
              <select value={form.chartMode || 'auto'} onChange={e => set('chartMode', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none">
                <option value="auto">{t('ai.set.chartAuto')}</option>
                <option value="manual">{t('ai.set.chartManual')}</option>
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                {t('ai.set.chartHint')}
              </p>
            </div>

            {/* Sembunyikan skor akurasi */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={!!form.hideAccuracy}
                  onChange={e => set('hideAccuracy', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                <span className="text-sm text-slate-700">{t('ai.set.hideAccuracy')}</span>
              </label>
              <p className="text-[11px] text-slate-500 mt-1">{t('ai.set.hideAccuracyHint')}</p>
            </div>

            {/* Lanjutan */}
            <div className="border-t border-slate-100 pt-3">
              <button type="button"
                onClick={() => setShowAdvanced(v => !v)}
                className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
                <ChevronDown style={{ width: 13, height: 13, transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                {t('ai.set.advanced')}
              </button>

              {showAdvanced && (
                <div className="mt-3 space-y-4">
                  {/* Frequency penalty */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-700">Frequency Penalty</span>
                      <span className="text-sm font-mono font-semibold text-slate-600">
                        {Number(form.frequencyPenalty ?? 0).toFixed(1)}
                      </span>
                    </div>
                    <input type="range" min="-2" max="2" step="0.1"
                      value={form.frequencyPenalty ?? 0}
                      onChange={e => set('frequencyPenalty', parseFloat(e.target.value))}
                      className="w-full accent-slate-500" />
                  </div>

                  {/* Presence penalty */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-700">Presence Penalty</span>
                      <span className="text-sm font-mono font-semibold text-slate-600">
                        {Number(form.presencePenalty ?? 0).toFixed(1)}
                      </span>
                    </div>
                    <input type="range" min="-2" max="2" step="0.1"
                      value={form.presencePenalty ?? 0}
                      onChange={e => set('presencePenalty', parseFloat(e.target.value))}
                      className="w-full accent-slate-500" />
                    <p className="text-[11px] text-slate-500 mt-1">
                      {t('ai.set.penaltyHint')}
                    </p>
                  </div>

                  {/* Seed */}
                  <div>
                    <label className="block text-sm text-slate-700 mb-1">{t('ai.set.seed')}</label>
                    <input type="number" step="1"
                      value={form.seed ?? ''}
                      placeholder={t('ai.set.seedPlaceholder')}
                      onChange={e => set('seed', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none" />
                    <p className="text-[11px] text-slate-500 mt-1">
                      {t('ai.set.seedHint')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── API Key (multi-key support) ─────────────────────────────────── */}
        {provider.requiresKey && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">API Key</label>
                {/* Badge jumlah key */}
                {keyCount > 0 && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold
                    ${keyCount > 1 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                    <Key style={{ width: 9, height: 9 }} />
                    {keyCount} key{keyCount > 1 ? 's' : ''}
                    {keyCount > 1 && <span className="ml-0.5">· auto-rotate</span>}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {provider.docsUrl && (
                  <a href={provider.docsUrl} target="_blank" rel="noreferrer"
                    className="text-xs text-brand-500 hover:text-brand-700 flex items-center gap-1">
                    {provider.docsLabel} <ExternalLink style={{ width: 10, height: 10 }} />
                  </a>
                )}
              </div>
            </div>

            {/* Key field: tersembunyi saat showKey=false, textarea saat showKey=true */}
            {!showKey ? (
              <div className="relative">
                <div className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-500 bg-slate-50 min-h-[42px] flex items-center font-mono">
                  {keyCount > 0
                    ? <span>{keyCount} key{keyCount > 1 ? `s` : ''} {t('ai.set.configured')}{keyCount > 1 ? ` (${t('ai.set.autoRotateLong')})` : ''}</span>
                    : <span className="text-slate-300 italic">{t('ai.set.notConfigured')}</span>
                  }
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {keyCount > 0 && (
                    <button type="button" onClick={handleCopyFirstKey} title={t('ai.set.copyFirstKey')}
                      className="p-1 text-slate-400 hover:text-slate-600">
                      {copied ? <Check style={{ width: 13, height: 13 }} className="text-emerald-500" /> : <Copy style={{ width: 13, height: 13 }} />}
                    </button>
                  )}
                  <button type="button" onClick={() => setShowKey(true)}
                    className="p-1 text-slate-400 hover:text-slate-600">
                    <Eye style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <textarea
                  value={form.apiKey}
                  onChange={e => set('apiKey', e.target.value)}
                  rows={3}
                  placeholder={`Key 1\nKey 2, Key 3\n${t('ai.set.keysPlaceholder')}`}
                  className="w-full px-3 py-2.5 pr-9 rounded-xl border border-slate-200 text-xs outline-none
                    focus:border-brand-400 focus:ring-2 focus:ring-brand-100 resize-none font-mono
                    text-slate-700 bg-white"
                />
                <button type="button" onClick={() => setShowKey(false)}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                  <EyeOff style={{ width: 14, height: 14 }} />
                </button>
              </div>
            )}

            {/* Info teks berganti berdasarkan jumlah key */}
            {keyCount <= 1 ? (
              <p className="text-xs text-slate-400 mt-1.5">
                🔒 {t('ai.set.keyInfoSingle')}
              </p>
            ) : (
              <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1.5">
                <CheckCircle2 style={{ width: 11, height: 11 }} />
                {t('ai.set.keyInfoMulti', { n: keyCount })}
              </p>
            )}
          </div>
        )}
        </>)}

        {/* System Prompt — per dashboard */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">System Prompt</label>
              {dashboardKey ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-medium">
                  <LayoutDashboard style={{ width: 9, height: 9 }} />
                  {dashboardLabel || dashboardKey}
                </span>
              ) : isGlobalPrompt ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium">
                  🌐 Global
                </span>
              ) : (
                <span className="ml-1 text-xs text-amber-500">{t('ai.set.promptGeneral')}</span>
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={loadDefaultPrompt} disabled={loadingPrompt}
                className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-700 px-2 py-1 rounded border border-brand-200 hover:border-brand-300 transition-colors">
                <RefreshCw style={{ width: 10, height: 10 }} className={loadingPrompt ? 'animate-spin' : ''} />
                {t('ai.set.default')}
              </button>
              <button onClick={() => setPromptVal('')}
                className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 px-2 py-1 rounded border border-slate-200">
                <RotateCcw style={{ width: 10, height: 10 }} /> {t('ai.set.reset')}
              </button>
            </div>
          </div>
          <textarea value={promptVal} onChange={e => setPromptVal(e.target.value)}
            disabled={!dashboardKey && !isGlobalPrompt}
            rows={7} placeholder={t('ai.set.promptPlaceholder')}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 resize-none text-slate-600 leading-relaxed disabled:bg-slate-50 disabled:text-slate-300" />
          <p className="text-xs text-slate-400 mt-1">
            💡 {isGlobalPrompt
              ? t('aiSet2.promptGlobalDipakaiSaatAda')
              : t('ai.set.promptScope', { dash: dashboardLabel || dashboardKey || '—', user: user?.username })}
          </p>
        </div>

        {/* Quick Prompts — per dashboard ATAU global */}
        {(dashboardKey || isGlobalPrompt) && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare style={{ width: 13, height: 13 }} className="text-slate-500" />
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Quick Prompts</label>
              {dashboardKey ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-medium">
                <LayoutDashboard style={{ width: 9, height: 9 }} />
                {dashboardLabel || dashboardKey}
              </span>
              ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium">
                🌐 Global
              </span>
              )}
            </div>

            <div className="flex gap-1 mb-3">
              {QP_LANGS.map(l => (
                <button key={l} onClick={() => setQpLang(l)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors
                    ${qpLang === l ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              {getItems(qpLang).map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <input type="text" value={item}
                    onChange={e => updateItem(qpLang, idx, e.target.value)}
                    placeholder={`Prompt ${idx + 1}`}
                    className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-100 text-slate-700" />
                  <button onClick={() => removeItem(qpLang, idx)}
                    className="p-1 text-slate-300 hover:text-red-400 transition-colors">
                    <Trash2 style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-2">
              <button onClick={() => addItem(qpLang)}
                className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 px-2 py-1 rounded border border-brand-200 hover:border-brand-300 transition-colors">
                <Plus style={{ width: 10, height: 10 }} /> {t('ai.set.add')}
              </button>
              <div className="flex items-center gap-1.5">
                <button onClick={() => clearItems(qpLang)}
                  className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-red-200 transition-colors">
                  <Trash2 style={{ width: 10, height: 10 }} /> {t('ai.set.clearAll')}
                </button>
                <button onClick={() => resetItems(qpLang)}
                  className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 px-2 py-1 rounded border border-slate-200 transition-colors">
                  <RotateCcw style={{ width: 10, height: 10 }} /> {t('ai.set.resetDefaultShort')}
                </button>
              </div>
            </div>
            {isCleared(qpLang) && (
              <p className="text-xs text-amber-500 mt-1.5">
                ⛔ {t('ai.set.quickPromptCleared')}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1.5">
              💡 {t('ai.set.quickPromptHint')}
            </p>
          </div>
        )}

        {error && (
          <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">{error}</div>
        )}
      </div>
      {/* Footer */}
      <div className="px-5 py-4 border-t border-slate-100 flex gap-2">
        <button onClick={onClose}
          className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all">
          {t('ai.set.cancel')}
        </button>
        <button onClick={handleSave} disabled={saving}
          className={`flex-1 py-2 px-4 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2
            ${saved ? 'bg-green-500 text-white' : 'bg-brand-600 hover:bg-brand-700 text-white'} disabled:opacity-60`}>
          {saving
            ? <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> {t('ai.set.saving')}</>
            : saved
            ? <><CheckCircle2 style={{ width: 14, height: 14 }} /> {t('ai.set.saved')}</>
            : <><Save style={{ width: 14, height: 14 }} /> {t('ai.set.save')}</>
          }
        </button>
      </div>
    </div>
  );
}
